<script setup lang="ts">
  import CallsService from '@/api/services/calls.service';
  import { useUi } from '@/composition/use-ui';
  import { CallType } from '@/enums/CallType';
  import { ICall } from '@/interfaces/ICall';
  import { useOrdersStore } from '@/stores/orders.store';
  import { computed, ref } from 'vue';

  const ordersStore = useOrdersStore();
  const ui = useUi();
  const isOpen = ref(false);
  const confirmingCallIds = ref([] as number[]);

  function callToText(call: ICall) {
    const table = call.table;

    switch (call.type) {
      case CallType.Bill:
        return `Mesa ${table?.name} pide la cuenta`;
      case CallType.Waiter:
        return `Mesa ${table?.name} pide al mesero`;
    }

    return call.type;
  }

  const formattedCalls = computed(() => {
    return ordersStore.calls?.map?.((call) => {
      return {
        icon: call.type === CallType.Bill ? 'mdi-receipt' : 'mdi-room-service',
        text: callToText(call),
        call,
      };
    });
  });

  async function confirmCall(call: ICall) {
    if (confirmingCallIds.value.includes(call.id)) return;

    try {
      confirmingCallIds.value.push(call.id);
      const res = await CallsService.confirm(call);
      ordersStore.removeCall(call.id);
    } catch (e) {
      ui.serverError(e, 'Error al confirmar llamado');
    } finally {
      confirmingCallIds.value = confirmingCallIds.value.filter(
        (id) => id !== call.id
      );
    }
  }
</script>

<template>
  <div class="calls-dropdown">
    <div v-if="formattedCalls?.length && !isOpen" class="new-indicator"></div>
    <v-menu v-model="isOpen" bottom :close-on-content-click="false" offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="calls-dropdown__btn"
          fab
          small
          depressed
          :color="!formattedCalls?.length ? 'grey' : 'primary'"
          :dark="!formattedCalls?.length"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>mdi-bell</v-icon>
        </v-btn>
      </template>
      <v-list class="call-list">
        <template v-if="!formattedCalls?.length">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>No hay llamados nuevos</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item
            v-for="callData in formattedCalls"
            :key="callData.call.id"
            class="call-item"
          >
            <v-list-item-content>
              <v-list-item-title>
                <v-icon class="pr-1" small>{{ callData.icon }}</v-icon>
                {{ callData.text }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                small
                color="primary"
                depressed
                rounded
                :loading="confirmingCallIds.includes(callData.call.id)"
                @click="confirmCall(callData.call)"
              >
                <v-icon left>mdi-check</v-icon>
                Aceptar
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
  .calls-dropdown {
    @apply tw-relative;

    .new-indicator {
      @apply tw-absolute tw-w-full tw-h-full tw-bg-orange-500 tw-rounded-full tw-left-0 tw-bottom-0 tw-right-0 tw-top-0 tw-animate-ping;
      z-index: 5;
    }

    .calls-dropdown__btn {
      z-index: 6;
    }
  }

  .call-list {
    @apply tw-divide-y tw-bg-orange-50 tw-divide-orange-300 tw-divide-dashed;
    max-height: 80vh;
    max-height: 80dvh;

    ::v-deep(.v-list-item) {
    }
  }
</style>
